import {
  ActionIcon,
  Alert,
  Button,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { type CreateReportInput, reportCategorySchema } from '@wwaave/shared/zod';
import { z } from 'zod';

import { ApiError, useApiForm } from '../hooks/useApiForm';
import { useAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';

/**
 * ReportButton — user-submitted moderation surface (T2.e).
 *
 * Renders a small affordance that opens a Mantine Modal carrying a
 * category-and-body form. Submits to POST /api/reports (built in T2.b) via
 * the standard `useApiForm` flow so CSRF, ApiError shaping, and the server
 * error -> Alert plumbing all come for free.
 *
 * Two variants:
 *   - 'icon'   small ActionIcon (used inline on message + review cards)
 *   - 'button' standard outline button (used in page headers)
 *
 * The button is hidden for anonymous viewers. The server also refuses
 * self-reports for public-profile types (venue, artist) and surfaces a 409;
 * the modal renders that message inline rather than dismissing.
 */

type Props = {
  subjectType: 'venue' | 'artist' | 'message' | 'review' | 'exchange';
  subjectId: string;
  /** Optional: short label used in the confirmation copy (e.g. "The Backroom"). */
  subjectLabel?: string;
  /** Optional: render variant. Defaults to 'button'. */
  variant?: 'icon' | 'button';
};

const SUBJECT_NOUN: Record<Props['subjectType'], string> = {
  venue: 'venue',
  artist: 'artist',
  message: 'message',
  review: 'review',
  exchange: 'exchange',
};

const CATEGORY_OPTIONS: { value: CreateReportInput['category']; label: string }[] = [
  { value: 'safety', label: 'Someone could get hurt' },
  { value: 'harassment', label: 'Harassment or abuse' },
  { value: 'spam_or_fake', label: 'Spam or fake account' },
  { value: 'misrepresentation', label: "Photos / details don't match reality" },
  { value: 'inappropriate_content', label: 'Inappropriate content' },
  { value: 'other', label: 'Something else' },
];

const BODY_MIN_CHARS = 20;

// Form-only sub-schema. The server validates against the full
// `createReportInputSchema` (incl. subject_type / subject_id), which we
// reshape via `transform` below. Keeping the body rule in lockstep with the
// canonical one — same length cap + same min-20-trimmed-chars refine — means
// the inline validator and the server validator can't drift.
const formSchema = z.object({
  category: reportCategorySchema,
  body: z
    .string()
    .max(2000, 'Keep it under 2000 characters.')
    .refine((s) => s.trim().length >= BODY_MIN_CHARS, {
      message: 'Tell us what happened. At least a sentence so we can triage.',
    }),
});

type FormValues = {
  category: CreateReportInput['category'] | '';
  body: string;
};

export function ReportButton({
  subjectType,
  subjectId,
  subjectLabel,
  variant = 'button',
}: Props): React.JSX.Element | null {
  const { user } = useAuth();
  const [opened, { open, close }] = useDisclosure(false);

  const { form, submit, submitting, serverError, setServerError } = useApiForm<
    FormValues,
    { ok: true; report: { id: string; status: 'open' } }
  >({
    endpoint: '/api/reports',
    schema: formSchema,
    initialValues: { category: '', body: '' },
    transform: (values) => ({
      subject_type: subjectType,
      subject_id: subjectId,
      category: values.category,
      body: values.body,
    }),
    onSuccess: () => {
      form.reset();
      close();
      showToast({
        kind: 'success',
        message: 'Thanks. The team will take a look.',
      });
    },
    mapError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          return "You've filed a few reports today. Come back tomorrow.";
        }
        // 409 (self-report), 404 (subject gone), 400 (bad body) all carry
        // a server-shaped message; surface as-is.
        return err.message;
      }
      return err.message;
    },
  });

  // Anonymous viewers have nothing to do here — the API requires a session
  // anyway, and hiding the affordance keeps the public surfaces clean.
  if (user === null) return null;

  const noun = SUBJECT_NOUN[subjectType];

  // Voice-aware confirmation line. Stays plain — no apology, no exclamation.
  const blurb = subjectLabel
    ? `If something about ${subjectLabel} feels wrong, tell us what you saw and we'll take a look.`
    : `If something here feels wrong, tell us what you saw and we'll take a look.`;

  const bodyValue = form.values.body;
  const bodyLen = bodyValue.trim().length;
  const remaining = BODY_MIN_CHARS - bodyLen;
  const bodyHint =
    remaining > 0
      ? `Add at least ${remaining} more character${remaining === 1 ? '' : 's'} so we can triage.`
      : `${bodyLen} character${bodyLen === 1 ? '' : 's'}. Thanks for the detail.`;

  const canSubmit =
    form.values.category !== '' && bodyLen >= BODY_MIN_CHARS && !submitting;

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip label={`Report this ${noun}`} withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label={`Report this ${noun}`}
            onClick={open}
          >
            ⚐
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button variant="outline" color="gray" size="sm" onClick={open}>
          Report
        </Button>
      )}

      <Modal
        opened={opened}
        onClose={() => {
          if (submitting) return;
          setServerError(null);
          close();
        }}
        title={`Report this ${noun}`}
        size="md"
        centered
        // 360px-friendly: Mantine clamps modal width to viewport on small
        // screens by default; size="md" gives breathing room on desktop.
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          noValidate
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              {blurb} It goes to the trust + safety team, and only they see what you write.
            </Text>

            <Text size="xs" c="dimmed">
              We act on a single credible report. Your report is confidential. Serious
              safety reports lead to immediate suspension pending review.
            </Text>

            <Select
              label="What's going on?"
              placeholder="Pick the closest fit"
              data={CATEGORY_OPTIONS}
              {...form.getInputProps('category')}
              required
              allowDeselect={false}
            />

            <Textarea
              label="Tell us what happened"
              placeholder="A sentence or two is enough. Dates, names, links, whatever helps."
              autosize
              minRows={4}
              maxRows={10}
              maxLength={2000}
              {...form.getInputProps('body')}
              description={bodyHint}
            />

            {serverError !== null && serverError.length > 0 ? (
              <Alert color="red" variant="light">
                {serverError}
              </Alert>
            ) : null}

            <Stack gap="xs">
              <Button type="submit" disabled={!canSubmit} loading={submitting}>
                Send report
              </Button>
              <Button
                type="button"
                variant="subtle"
                color="gray"
                onClick={() => {
                  if (submitting) return;
                  setServerError(null);
                  close();
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
